export type SourceLanguage =
  | "html"
  | "css"
  | "javascript"
  | "jsx"
  | "typescript"
  | "tsx"
  | "json"
  | "jsonc"
  | "yaml"
  | "toml"
  | "markdown"
  | "bash"
  | "sql"
  | "python"
  | "go"
  | "rust"
  | "java"
  | "c"
  | "cpp"
  | "dockerfile"
  | "makefile"
  | "dotenv"
  | "ini"
  | "xml"
  | "hcl"
  | "graphql"
  | "diff"
  | "plaintext";

const SPECIAL_BASENAME: Record<string, SourceLanguage> = {
  dockerfile: "dockerfile",
  containerfile: "dockerfile",
  makefile: "makefile",
  gnumakefile: "makefile",
  ".env": "dotenv",
  ".editorconfig": "ini",
};

const COMPOUND_EXTENSION: ReadonlyArray<readonly [string, SourceLanguage]> = [
  [".tfvars.json", "json"],
  [".tf.json", "json"],
  [".d.mts", "typescript"],
  [".d.cts", "typescript"],
  [".d.ts", "typescript"],
];

const EXTENSION: Record<string, SourceLanguage> = {
  html: "html",
  htm: "html",
  css: "css",
  js: "javascript",
  mjs: "javascript",
  cjs: "javascript",
  jsx: "jsx",
  ts: "typescript",
  mts: "typescript",
  cts: "typescript",
  tsx: "tsx",
  json: "json",
  jsonc: "jsonc",
  yaml: "yaml",
  yml: "yaml",
  toml: "toml",
  md: "markdown",
  markdown: "markdown",
  sh: "bash",
  bash: "bash",
  zsh: "bash",
  sql: "sql",
  py: "python",
  go: "go",
  rs: "rust",
  java: "java",
  c: "c",
  h: "c",
  cc: "cpp",
  cpp: "cpp",
  cxx: "cpp",
  hh: "cpp",
  hpp: "cpp",
  hxx: "cpp",
  mk: "makefile",
  env: "dotenv",
  ini: "ini",
  cfg: "ini",
  editorconfig: "ini",
  xml: "xml",
  xsd: "xml",
  xsl: "xml",
  xslt: "xml",
  svg: "xml",
  hcl: "hcl",
  tf: "hcl",
  tfvars: "hcl",
  graphql: "graphql",
  gql: "graphql",
  diff: "diff",
  patch: "diff",
  txt: "plaintext",
  text: "plaintext",
  log: "plaintext",
};

const MIME: Record<string, SourceLanguage> = {
  "text/html": "html",
  "application/xhtml+xml": "html",
  "text/css": "css",
  "text/javascript": "javascript",
  "application/javascript": "javascript",
  "text/typescript": "typescript",
  "application/typescript": "typescript",
  "application/json": "json",
  "text/json": "json",
  "application/jsonc": "jsonc",
  "text/yaml": "yaml",
  "application/yaml": "yaml",
  "application/x-yaml": "yaml",
  "application/toml": "toml",
  "text/markdown": "markdown",
  "text/x-shellscript": "bash",
  "application/sql": "sql",
  "text/x-python": "python",
  "text/x-go": "go",
  "text/x-rust": "rust",
  "text/x-java-source": "java",
  "text/x-c": "c",
  "text/x-c++src": "cpp",
  "application/xml": "xml",
  "text/xml": "xml",
  "application/graphql": "graphql",
  "text/x-diff": "diff",
};

export function sourceLanguageFor(file: { name: string; mediaType: string }): SourceLanguage {
  const name = file.name.replaceAll("\\", "/").split("/").at(-1)?.toLowerCase() ?? "";
  const special = SPECIAL_BASENAME[name];
  if (special) return special;
  if (name.startsWith(".env.")) return "dotenv";
  if (name.startsWith("dockerfile.") || name.startsWith("containerfile.")) return "dockerfile";
  for (const [ending, language] of COMPOUND_EXTENSION) {
    if (name.endsWith(ending)) return language;
  }
  const extension = name.includes(".") ? (name.split(".").at(-1) ?? "") : "";
  const byExtension = EXTENSION[extension];
  if (byExtension) return byExtension;
  const mime = file.mediaType.split(";", 1)[0]?.trim().toLowerCase() ?? "";
  return MIME[mime] ?? "plaintext";
}
